/**
 * Sistema de unidades y conversiones.
 *
 * REGLA NORMATIVA DEL PROYECTO (Req. 5.3):
 *
 *   Toda cantidad se almacena en la UNIDAD BASE de su dimension:
 *   gramos para masa, mililitros para volumen, piezas para conteo.
 *
 * La unidad de captura es unicamente presentacion. Internamente todo son
 * gramos, y comparar 800 g contra 1 kg deja de ser un problema.
 *
 * Las tres dimensiones son mutuamente incompatibles: convertir masa a volumen
 * lanza error en lugar de inventar un numero (Req. 5.5). La unica excepcion es
 * el factor de equivalencia declarado por insumo (Req. 5.6), que resuelve el
 * caso real de comprar por pieza o por paquete y cocinar por peso.
 */

import { ErrorConversionUnidades, ErrorValidacion } from './errors';

export type Dimension = 'masa' | 'volumen' | 'conteo';
export type UnidadBase = 'g' | 'ml' | 'pza';

export interface DefinicionUnidad {
  readonly clave: string;
  readonly nombre: string;
  readonly plural: string;
  readonly abreviatura: string;
  readonly dimension: Dimension;
  /** Cuantas unidades base equivale una unidad de esta clase. */
  readonly factorABase: number;
}

/** Equivalencia declarada por insumo. Ej: 1 pieza de pechuga = 250 g */
export interface FactorEquivalencia {
  readonly unidadCompra: string;
  readonly cantidadBase: number;
}

/** Datos del insumo que la conversion necesita conocer. */
export interface ContextoInsumo {
  readonly nombre?: string;
  readonly dimension: Dimension;
  readonly factorEquivalencia: FactorEquivalencia | null;
}

export const UNIDAD_BASE: Readonly<Record<Dimension, UnidadBase>> = {
  masa: 'g',
  volumen: 'ml',
  conteo: 'pza',
};

export const NOMBRE_DIMENSION: Readonly<Record<Dimension, string>> = {
  masa: 'Peso',
  volumen: 'Volumen',
  conteo: 'Conteo',
};

// ---------------------------------------------------------------------------
// Catalogo de unidades (Req. 5.4)
// ---------------------------------------------------------------------------

function def(
  clave: string,
  nombre: string,
  plural: string,
  abreviatura: string,
  dimension: Dimension,
  factorABase: number,
): DefinicionUnidad {
  return { clave, nombre, plural, abreviatura, dimension, factorABase };
}

export const UNIDADES: Readonly<Record<string, DefinicionUnidad>> = {
  // Masa, base: gramo
  kg: def('kg', 'kilogramo', 'kilogramos', 'kg', 'masa', 1000),
  g: def('g', 'gramo', 'gramos', 'g', 'masa', 1),
  lb: def('lb', 'libra', 'libras', 'lb', 'masa', 453.592),
  oz: def('oz', 'onza', 'onzas', 'oz', 'masa', 28.3495),

  // Volumen, base: mililitro
  l: def('l', 'litro', 'litros', 'l', 'volumen', 1000),
  ml: def('ml', 'mililitro', 'mililitros', 'ml', 'volumen', 1),
  taza: def('taza', 'taza', 'tazas', 'taza', 'volumen', 240),
  cda: def('cda', 'cucharada', 'cucharadas', 'cda', 'volumen', 15),
  cdta: def('cdta', 'cucharadita', 'cucharaditas', 'cdta', 'volumen', 5),

  /**
   * Conteo, base: pieza.
   *
   * `paquete` y `barra` son unidades de conteo por derecho propio, con factor 1.
   *
   * MOTIVO: si un insumo se compra y se usa en la misma unidad, no hace falta
   * ninguna conversion. La mantequilla que se compra por barra y la receta pide
   * en barras funciona sin declarar cuanto pesa cada una. Exigir ese dato antes
   * de poder capturar una compra era friccion sin beneficio.
   *
   * El factor de equivalencia por insumo sigue existiendo, y se pide solo
   * cuando de verdad se necesita: cuando una receta pide gramos de algo que se
   * compra por pieza o por paquete (Req. 5.6, 5.7).
   */
  pza: def('pza', 'pieza', 'piezas', 'pza', 'conteo', 1),
  docena: def('docena', 'docena', 'docenas', 'docena', 'conteo', 12),
  paquete: def('paquete', 'paquete', 'paquetes', 'paquete', 'conteo', 1),
  barra: def('barra', 'barra', 'barras', 'barra', 'conteo', 1),
  bolsa: def('bolsa', 'bolsa', 'bolsas', 'bolsa', 'conteo', 1),
  lata: def('lata', 'lata', 'latas', 'lata', 'conteo', 1),
  manojo: def('manojo', 'manojo', 'manojos', 'manojo', 'conteo', 1),
};

/** Unidades que pueden llevar factor de equivalencia hacia otra dimension. */
export const UNIDADES_CON_EQUIVALENCIA: readonly string[] = [
  'pza',
  'docena',
  'paquete',
  'barra',
  'bolsa',
  'lata',
  'manojo',
];

// ---------------------------------------------------------------------------
// Consultas del catalogo
// ---------------------------------------------------------------------------

export function unidadesDe(dimension: Dimension): DefinicionUnidad[] {
  return Object.values(UNIDADES).filter((u) => u.dimension === dimension);
}

export function esUnidadConocida(unidad: string): boolean {
  return unidad in UNIDADES;
}

export function etiquetaUnidad(unidad: string, cantidad = 1): string {
  const definicion = UNIDADES[unidad];
  if (definicion === undefined) return unidad;
  return Math.abs(cantidad) === 1 ? definicion.nombre : definicion.plural;
}

/** Dimension a la que pertenece una unidad. Sirve para deducirla al crear un insumo. */
export function dimensionDeUnidad(unidad: string): Dimension | null {
  return UNIDADES[unidad]?.dimension ?? null;
}

export function abreviaturaUnidad(unidad: string): string {
  return UNIDADES[unidad]?.abreviatura ?? unidad;
}

/**
 * Unidades en las que se puede capturar un insumo: las de su dimension mas la
 * de su factor de equivalencia, si tiene uno.
 */
export function unidadesDisponibles(ctx: ContextoInsumo): string[] {
  const claves = unidadesDe(ctx.dimension).map((u) => u.clave);
  const equivalencia = ctx.factorEquivalencia?.unidadCompra;
  if (equivalencia !== undefined && !claves.includes(equivalencia)) {
    claves.push(equivalencia);
  }
  return claves;
}

// ---------------------------------------------------------------------------
// Normalizacion
// ---------------------------------------------------------------------------

const DECIMALES_CANTIDAD = 3;

/**
 * Normaliza una cantidad en unidad base a 3 decimales: precision de un
 * miligramo, muy por encima de lo que distingue una bascula de cocina.
 * Evita que el punto flotante arrastre colas como 0.30000000000000004.
 */
export function normalizarCantidad(cantidad: number): number {
  if (!Number.isFinite(cantidad)) {
    throw new ErrorValidacion('La cantidad capturada no es un numero valido.');
  }
  const factor = 10 ** DECIMALES_CANTIDAD;
  return Math.round(cantidad * factor) / factor;
}

// ---------------------------------------------------------------------------
// Conversiones
// ---------------------------------------------------------------------------

function nombreInsumo(ctx: ContextoInsumo): string {
  return ctx.nombre !== undefined && ctx.nombre !== '' ? `"${ctx.nombre}"` : 'el insumo';
}

/**
 * Convierte una cantidad capturada a la unidad base del insumo.
 *
 * Orden de resolucion:
 *   1. Ya es la unidad base                  -> tal cual
 *   2. Unidad global de la misma dimension   -> factor universal
 *   3. Coincide con el factor del insumo     -> factor declarado
 *   4. Cualquier otro caso                   -> error explicito
 *
 * El paso 3 va DESPUES del 2 y ANTES del error: es lo que permite que un
 * insumo de dimension masa se capture en piezas cuando declara cuanto pesa
 * cada pieza.
 */
export function aBase(cantidad: number, unidad: string, ctx: ContextoInsumo): number {
  if (!Number.isFinite(cantidad)) {
    throw new ErrorValidacion('La cantidad capturada no es un numero valido.');
  }

  const base = UNIDAD_BASE[ctx.dimension];
  if (unidad === base) return normalizarCantidad(cantidad);

  const global = UNIDADES[unidad];
  if (global !== undefined && global.dimension === ctx.dimension) {
    return normalizarCantidad(cantidad * global.factorABase);
  }

  const equivalencia = ctx.factorEquivalencia;
  if (equivalencia !== null && equivalencia.unidadCompra === unidad) {
    return normalizarCantidad(cantidad * equivalencia.cantidadBase);
  }

  // A partir de aqui la conversion no es posible. Los mensajes indican
  // exactamente que falta, para que se pueda corregir sin adivinar.
  if (global !== undefined) {
    throw new ErrorConversionUnidades(
      `No se puede convertir ${etiquetaUnidad(unidad, cantidad)} a ${base} para ${nombreInsumo(ctx)}: ` +
        `son medidas de distinta naturaleza (${NOMBRE_DIMENSION[global.dimension].toLowerCase()} ` +
        `contra ${NOMBRE_DIMENSION[ctx.dimension].toLowerCase()}). ` +
        `Define cuanto equivale 1 ${etiquetaUnidad(unidad)} en ${base}.`,
    );
  }

  throw new ErrorConversionUnidades(`La unidad "${unidad}" no existe en el catalogo.`);
}

/** Convierte de unidad base a una unidad de presentacion. */
export function desdeBase(cantidadBase: number, unidad: string, ctx: ContextoInsumo): number {
  if (!Number.isFinite(cantidadBase)) {
    throw new ErrorValidacion('La cantidad no es un numero valido.');
  }

  const base = UNIDAD_BASE[ctx.dimension];
  if (unidad === base) return normalizarCantidad(cantidadBase);

  const global = UNIDADES[unidad];
  if (global !== undefined && global.dimension === ctx.dimension) {
    return normalizarCantidad(cantidadBase / global.factorABase);
  }

  const equivalencia = ctx.factorEquivalencia;
  if (equivalencia !== null && equivalencia.unidadCompra === unidad) {
    if (equivalencia.cantidadBase === 0) {
      throw new ErrorConversionUnidades(
        `El factor de equivalencia de ${nombreInsumo(ctx)} no puede ser cero.`,
      );
    }
    return normalizarCantidad(cantidadBase / equivalencia.cantidadBase);
  }

  throw new ErrorConversionUnidades(
    `No se puede expresar ${nombreInsumo(ctx)} en ${etiquetaUnidad(unidad, 2)}.`,
  );
}

// ---------------------------------------------------------------------------
// Presentacion de cantidades
// ---------------------------------------------------------------------------

/** Formatea un numero sin ceros de relleno. `1.500` da `"1.5"` */
export function formatearNumero(valor: number, maxDecimales = DECIMALES_CANTIDAD): string {
  const redondeado = Number(valor.toFixed(maxDecimales));
  return new Intl.NumberFormat('es-MX', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimales,
  }).format(redondeado);
}

/**
 * Formatea una cantidad en unidad base eligiendo la unidad mas legible
 * (Req. 14.8). 1500 g se muestra como "1.5 kg"; 640 g como "640 g".
 */
export function formatearCantidadBase(
  cantidadBase: number,
  dimension: Dimension,
  /**
   * Unidad con la que nombrar las cantidades de conteo. Sin esto, 8 barras de
   * mantequilla se leerian como "8 piezas", que es correcto pero pierde
   * informacion. Con la unidad del insumo se lee "8 barras".
   */
  unidadConteo = 'pza',
): string {
  const absoluto = Math.abs(cantidadBase);

  if (dimension === 'masa') {
    if (absoluto >= 1000) return `${formatearNumero(cantidadBase / 1000)} kg`;
    return `${formatearNumero(cantidadBase)} g`;
  }

  if (dimension === 'volumen') {
    if (absoluto >= 1000) return `${formatearNumero(cantidadBase / 1000)} l`;
    return `${formatearNumero(cantidadBase)} ml`;
  }

  const cantidad = normalizarCantidad(cantidadBase);
  // Solo las unidades de conteo con factor 1 nombran la cantidad base tal cual;
  // una docena vale 12 y no serviria como etiqueta.
  const etiqueta =
    UNIDADES[unidadConteo]?.dimension === 'conteo' && UNIDADES[unidadConteo]?.factorABase === 1
      ? unidadConteo
      : 'pza';
  return `${formatearNumero(cantidad)} ${etiquetaUnidad(etiqueta, cantidad)}`;
}

/** Formatea una cantidad base en una unidad concreta. */
export function formatearEnUnidad(
  cantidadBase: number,
  unidad: string,
  ctx: ContextoInsumo,
): string {
  const cantidad = desdeBase(cantidadBase, unidad, ctx);
  // Las unidades de conteo se leen mejor con su nombre completo ("3 barras");
  // las de peso y volumen con su abreviatura ("12 kg").
  const etiqueta =
    UNIDADES[unidad]?.dimension === 'conteo'
      ? etiquetaUnidad(unidad, cantidad)
      : abreviaturaUnidad(unidad);
  return `${formatearNumero(cantidad)} ${etiqueta}`;
}

/**
 * Redondea una cantidad faltante hacia arriba al multiplo practico de compra.
 * Es una SUGERENCIA informativa: no restringe cuanto se compra (Req. 14.9, 14.10).
 */
export function sugerirCantidadCompra(
  faltanteBase: number,
  ctx: ContextoInsumo,
): { cantidadBase: number; unidad: string; cantidad: number } | null {
  if (faltanteBase <= 0) return null;

  const equivalencia = ctx.factorEquivalencia;
  if (equivalencia === null || equivalencia.cantidadBase <= 0) return null;

  const paquetes = Math.ceil(faltanteBase / equivalencia.cantidadBase);
  return {
    cantidadBase: normalizarCantidad(paquetes * equivalencia.cantidadBase),
    unidad: equivalencia.unidadCompra,
    cantidad: paquetes,
  };
}
